import { HttpRequest } from '@angular/common/http';
import { encryptApis } from '../../util/Encrypted-Apis';
import { SKIP_CRYPTO } from './crypto.tokens';
import { isPlaintextPath, isRequestPlaintextPath } from './plaintext-paths';

/**
 * How the app talks to whichever `client-service` build the environment points at.
 *
 * - `global` — the service applies `PayloadEncryptionAdvice` / `PayloadDecryptionAdvice`
 *   to every controller (`cs-svc-enc`). Encrypt by default, opt out via
 *   `plaintext-paths.ts`.
 * - `legacy` — the older service that encrypts only the handful of endpoints listed
 *   in `util/Encrypted-Apis.ts` (`cs-svc` / `dc-svc` on qa, uat, prod).
 * - `off` — pass every payload through untouched.
 */
export type CryptoMode = 'global' | 'legacy' | 'off';

/** What to do with one request. The two directions are decided independently, mirroring the two backend advices. */
export interface CryptoPolicy {
  encryptRequest: boolean;
  decryptResponse: boolean;
}

const PASSTHROUGH: CryptoPolicy = { encryptRequest: false, decryptResponse: false };

/**
 * Request path relative to the API root, or `null` when the request isn't aimed at
 * our backend at all.
 *
 * Returning `null` for foreign origins is what keeps Alloy, MS Graph, the NY Fed
 * rates feed, Azure blob storage and the rest of the third-party calls from being
 * mangled — they must never be touched, whatever the mode.
 */
export function resolveApiPath(url: string, apiUrl: string): string | null {
  // Some environment files carry a stray leading space in `apiUrl`, and it is handed
  // to `ApiModule.forRoot()` verbatim — so `RequestBuilder` concatenates it into
  // `request.url` and it reaches us on BOTH sides. Trimming only the root would make
  // `startsWith` fail and silently drop every request to passthrough.
  const root = (apiUrl ?? '').trim().replace(/\/+$/, '');
  const target = (url ?? '').trim();
  if (!root || !target.startsWith(root)) {
    return null;
  }
  const rest = target.slice(root.length);
  // Guard against a sibling service sharing the prefix (`/cs-svc-enc-internal`).
  if (rest && !rest.startsWith('/') && !rest.startsWith('?')) {
    return null;
  }
  const path = rest.split(/[?#]/)[0];
  return path || '/';
}

/** Bodies that must reach the server byte-for-byte — multipart uploads and raw binary. */
function isEncryptableBody(body: unknown): boolean {
  if (body === null || body === undefined) {
    return false;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return false;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return false;
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return false;
  }
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
    return false;
  }
  // A body the caller already wrapped itself — encrypting it twice would break it.
  if (typeof body === 'object' && 'encryptedData' in (body as Record<string, unknown>)) {
    return false;
  }
  return true;
}

/** Responses Angular hands back as binary; forcing them to `text` would corrupt the bytes. */
function isBinaryResponse(responseType: string): boolean {
  return responseType === 'blob' || responseType === 'arraybuffer';
}

/** Legacy allowlist matching, kept byte-identical to the original `CommonService.modifyApiRequest`. */
function matchesLegacyBucket(url: string, endpoints: readonly string[]): boolean {
  return endpoints.some((endpoint) => url.includes(`/${endpoint}`));
}

/**
 * Decide what happens to one request.
 *
 * Order matters: origin first (so third-party calls leave untouched), then the
 * caller's explicit opt-out, then the backend's plaintext contract, then the
 * structural guards on body and response type.
 */
export function resolveCryptoPolicy(
  request: HttpRequest<unknown>,
  apiUrl: string,
  mode: CryptoMode
): CryptoPolicy {
  if (mode === 'off') {
    return PASSTHROUGH;
  }

  const path = resolveApiPath(request.url, apiUrl);
  if (path === null) {
    return PASSTHROUGH;
  }

  if (request.context.get(SKIP_CRYPTO)) {
    return PASSTHROUGH;
  }

  const bodyIsEncryptable = isEncryptableBody(request.body);
  const responseIsDecryptable = !isBinaryResponse(request.responseType);

  if (mode === 'legacy') {
    const url = request.url;
    if (matchesLegacyBucket(url, encryptApis.requestAndResponseEncript)) {
      return { encryptRequest: bodyIsEncryptable, decryptResponse: responseIsDecryptable };
    }
    if (matchesLegacyBucket(url, encryptApis.requestEncriptOnly)) {
      return { encryptRequest: bodyIsEncryptable, decryptResponse: false };
    }
    if (matchesLegacyBucket(url, encryptApis.responseEncriptOnly)) {
      return { encryptRequest: false, decryptResponse: responseIsDecryptable };
    }
    return PASSTHROUGH;
  }

  // mode === 'global'
  if (isPlaintextPath(path)) {
    return PASSTHROUGH;
  }

  return {
    encryptRequest: bodyIsEncryptable && !isRequestPlaintextPath(path),
    decryptResponse: responseIsDecryptable,
  };
}
